import { Link, Typography } from "@fullscript/aviary-web";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { l } from "@aviary/locales/i18n";
import { sharedRoutes } from "@shared/data/sharedRoutes";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";

import { getSubtleFootnoteProps } from "../getSubtleFootnoteProps";

import * as styles from "./FooterLinkList.styles";

interface Props {
  type?: "basic" | "product" | "checkout" | "labs";
  isWholesale?: boolean;
  isInverseTheme?: boolean;
}

const FooterLinkList = ({ isWholesale, type = "basic", isInverseTheme = false }: Props) => {
  const { t } = useTranslation();
  const { isUS, isPatient } = useSharedGlobalConfig();

  const getShippingPolicyLink = () => {
    return isWholesale
      ? sharedRoutes.external.shippingPolicyWholesale
      : sharedRoutes.external.shippingPolicyRetail;
  };

  const getTermsOfServiceLink = () => {
    return isPatient
      ? sharedRoutes.external.termsOfServicePatients
      : sharedRoutes.external.termsOfService;
  };

  const renderLinkItem = (href: string, label: string, key: string) => (
    <li key={key}>
      <Typography {...getSubtleFootnoteProps(isInverseTheme)}>
        <Link
          hideExternalIcon
          hideUnderline
          href={href}
          intention={isInverseTheme ? "inverse" : undefined}
        >
          {label}
        </Link>
      </Typography>
    </li>
  );

  const linkItems: ReactNode[] = [
    renderLinkItem(getTermsOfServiceLink(), t(l.aviary.footer.TermsOfService), "terms-of-service"),
    renderLinkItem(
      sharedRoutes.external.privacyPolicy,
      t(l.aviary.footer.PrivacyStatement),
      "privacy-statement"
    ),
  ];

  if (type !== "checkout" && !isUS) {
    linkItems.push(
      renderLinkItem(
        sharedRoutes.external.accessibility,
        t(l.aviary.footer.AccessibilityPolicy),
        "accessibility-policy"
      )
    );
  }

  if (isUS) {
    linkItems.push(
      renderLinkItem(
        sharedRoutes.external.privacyRightsNotice,
        t(l.aviary.footer.PrivacyRightsNotice),
        "privacy-rights-notice"
      )
    );
  }

  if (type === "checkout") {
    linkItems.push(
      renderLinkItem(getShippingPolicyLink(), t(l.aviary.footer.ShippingPolicy), "shipping-policy"),
      renderLinkItem(
        sharedRoutes.external.refundPolicy,
        t(l.aviary.footer.RefundPolicy),
        "refund-policy"
      )
    );
  }

  linkItems.push(
    renderLinkItem(
      sharedRoutes.external.support,
      t(l.aviary.footer.HelpAndSupport),
      "help-and-support"
    )
  );

  const listStyles = [styles.list, isInverseTheme && styles.listInverseRight];

  return <ul css={listStyles}>{linkItems}</ul>;
};

export { FooterLinkList };
